import type { FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import CircleCheck from '@/assets/icons/operational-dashboard/circle-check.svg?component';
import { Badge, type BadgeTone } from '@/components/atoms/Badge';
import { Card } from '@/components/atoms/Card';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { ActivityLogEntry as ActivityLogEntryType } from '@/domain/operationalDashboard';
import {
    renderActivityLogDescription,
    renderActivityLogTitle,
} from '@/domain/operationalDashboard';
import { formatDateTimeWorded } from '@/utils/date';

import styles from './ActivityLogEntry.module.scss';

interface ActivityLogEntryProps {
    entry: ActivityLogEntryType;
}

const ACTION_BADGE_TONE: Record<string, BadgeTone> = {
    task_completed: 'success',
    task_ignored: 'neutral',
    task_unignored: 'accent',
    task_created: 'accent',
    task_auto_resolved: 'success',
    task_reactivated: 'danger',
    manual_entry: 'warning',
};

const ACTION_BADGE_ICON: Record<string, FunctionComponent<SVGProps<SVGSVGElement>>> = {
    task_completed: CircleCheck,
};

export default function ActivityLogEntry(props: ActivityLogEntryProps) {
    const { entry } = props;
    const { t } = useTranslation();

    const badgeTone = ACTION_BADGE_TONE[entry.action] ?? 'neutral';
    const badgeIcon = ACTION_BADGE_ICON[entry.action];
    const formattedDate = formatDateTimeWorded(entry.createdAt);
    const title = renderActivityLogTitle(entry, t);
    const description = renderActivityLogDescription(entry, t);

    return (
        <Card variant="subtle" radius="sm" padding="none" className={styles.entry}>
            <div className={styles.titleRow}>
                <span className={styles.title}>{title}</span>
                <Badge
                    tone={badgeTone}
                    icon={
                        badgeIcon && (
                            <SvgIcon
                                icon={badgeIcon}
                                size="custom"
                                customSize={14}
                                color="currentColor"
                            />
                        )
                    }>
                    {t(`operationalDashboard.action.${entry.action}`)}
                </Badge>
            </div>
            {description && <div className={styles.description}>{description}</div>}
            <div className={styles.meta}>
                {entry.cageName &&
                    t('operationalDashboard.activityLog.pen', { name: entry.cageName })}
                {entry.cageName && entry.bioscopeId && <> &middot; </>}
                {entry.bioscopeId &&
                    t('operationalDashboard.activityLog.bioscope', { id: entry.bioscopeId })}
            </div>
            <div className={styles.meta}>
                {formattedDate}
                {entry.performedByName && (
                    <>
                        {' '}
                        {t('operationalDashboard.activityLog.performedBy', {
                            name: entry.performedByName,
                        })}
                    </>
                )}
            </div>
            {entry.comment && <div className={styles.comment}>{entry.comment}</div>}
        </Card>
    );
}
